import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/lib/apiClient';

interface HourBucket {
  hour: number;
  sends: number;
  opens: number;
  openRate: number;
}

interface SendTimeAnalytics {
  byHour: HourBucket[];
  predictedBestHourByType: Record<string, number>;
  trainedAt: string | null;
}

function HourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function RateBar({ rate }: { rate: number }) {
  const pct = Math.round(rate * 100);
  return (
    <div className="h-2 w-full rounded bg-muted">
      <div className="h-2 rounded bg-primary" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SendTimeAnalyticsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<SendTimeAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/api/v1/analytics/send-time')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const hasSends = data?.byHour.some((b) => b.sends > 0);

  return (
    <div className="space-y-6 p-6">
      <Breadcrumbs
        crumbs={[
          { label: t('nav.analytics'), to: '/analytics' },
          { label: t('sendTime.title') },
        ]}
      />
      <h1 className="text-2xl font-bold">{t('sendTime.title')}</h1>

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !data || !hasSends ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">{t('sendTime.no_data')}</p>
        </Card>
      ) : (
        <>
          {/* Predicted best hours */}
          {Object.keys(data.predictedBestHourByType).length > 0 && (
            <Card className="p-6">
              <h2 className="font-semibold">{t('sendTime.best_hour_label')}</h2>
              <div className="mt-3 flex flex-wrap gap-4">
                {Object.entries(data.predictedBestHourByType).map(([type, hour]) => (
                  <div key={type} className="rounded border border-border px-4 py-2 text-center">
                    <div className="text-2xl font-bold text-primary">{HourLabel(hour)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t(`sendTime.${type.toLowerCase()}_best`)}
                    </div>
                  </div>
                ))}
              </div>
              {data.trainedAt && (
                <p className="mt-3 text-xs text-muted-foreground">
                  {t('sendTime.trained_at')}:{' '}
                  {new Date(data.trainedAt).toLocaleString()}
                </p>
              )}
            </Card>
          )}

          {/* Open rate by hour table */}
          <Card className="p-6">
            <h2 className="mb-4 font-semibold">{t('sendTime.by_hour')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-2 pr-4 text-left">{t('sendTime.hour')}</th>
                    <th className="py-2 pr-4 text-right">{t('sendTime.sends')}</th>
                    <th className="py-2 pr-4 text-right">{t('sendTime.opens')}</th>
                    <th className="py-2 pr-4 text-right">{t('sendTime.open_rate')}</th>
                    <th className="w-40 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.byHour
                    .filter((b) => b.sends > 0)
                    .map((b) => (
                      <tr key={b.hour}>
                        <td className="py-2 pr-4 font-mono">{HourLabel(b.hour)}</td>
                        <td className="py-2 pr-4 text-right">{b.sends}</td>
                        <td className="py-2 pr-4 text-right">{b.opens}</td>
                        <td className="py-2 pr-4 text-right">
                          {(b.openRate * 100).toFixed(1)}%
                        </td>
                        <td className="py-2">
                          <RateBar rate={b.openRate} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
